import React from "react";
import { Lg, Xs, Pill } from "components/common";
import moment from "moment";
import creditUtilizationBlur from "assets/utilization-blur.png";
import creditAgeBlur from "assets/credit-age-blur.png";
import creditCheckBlur from "assets/credit-check-blur.png";
import openAccountsBlur from "assets/open-accounts-blur.png";
import {
  getRating,
  RATINGS,
  FACTORS,
  FACTOR_DESCRIPTION,
} from "./CreditFactorsConfig";

const BLUR_IMAGES = {
  [FACTORS.UTILIZATION]: creditUtilizationBlur,
  [FACTORS.AGE]: creditAgeBlur,
  [FACTORS.CHECKS]: creditCheckBlur,
  [FACTORS.OPEN_ACCOUNTS]: openAccountsBlur,
};

const SUPERSCRIPT = (value) => (
  <span className="align-text-top text-lg ml-1">{value}</span>
);

const CREDIT_UTILIZATION_FORMATTED = (percentage) => (
  <>
    {Math.round(percentage * 100)}
    {SUPERSCRIPT("%")}
  </>
);
const CREDIT_AGE_FORMATTED = (age) => {
  return (
    <span className="flex items-start">
      {age?.years()}
      <span className="text-xxs mr-1">{SUPERSCRIPT("yrs")}</span>
      {age?.months()}
      <span className="text-xxs">{SUPERSCRIPT("mos")}</span>
    </span>
  );
};

const PILL_CONFIG = {
  "Excellent": {
    pillColor: "bg-black",
    fontColor: "text-white",
    label: RATINGS.EXCELLENT,
  },
  "Good": {
    pillColor: "bg-green-primary",
    fontColor: "text-black",
    label: RATINGS.GOOD,
  },
  "Fair": {
    pillColor: "bg-green-secondary",
    fontColor: "text-black",
    label: RATINGS.FAIR,
  },
  "Needs Work": {
    pillColor: "bg-fog-gray",
    fontColor: "text-black",
    label: RATINGS.NEEDS_WORK,
  },
  "N/A": {
    pillColor: "bg-fog-gray",
    fontColor: "text-black",
    label: "N/A",
  },
};

const CreditMonitoringPill = ({ rating }) => {
  return (
    <Pill
      pillColor={PILL_CONFIG[rating].pillColor}
      fontColor={PILL_CONFIG[rating].fontColor}
      className="py-1 mb-4 text-xs font-semibold"
      label={PILL_CONFIG[rating].label}
    />
  );
};

/**
 * @param {Object} props
 * @param {string} props.name
 * @param {number} props.value
 * @param {string} props.valueFormatted
 * @param {string} props.description
 * @param {boolean} props.isMobile
 * @param {boolean} props.isBlurred
 * @returns
 */
const Factor = (props) => {
  const {
    name,
    value,
    valueFormatted,
    description,
    isMobile = false,
    isBlurred = true,
  } = props;
  return (
    <div
      className={`flex flex-col px-4 py-6 items-center ${
        isMobile ? "w-1/2" : "w-1/4"
      }`}
    >
      <h3
        className="mb-4 text-center"
        style={{ minHeight: isMobile ? 36 : 48 }}
      >
        {name}
      </h3>
      <div className="mb-4 min-h-9">
        {isBlurred ? (
          <img src={BLUR_IMAGES[name]} alt={`${name} blur`} style={{ minHeight: 62 }} />
        ) : (
          <p className="text-2xl">{valueFormatted || value}</p>
        )}
      </div>

      {!!name && value !== null && (
        <CreditMonitoringPill rating={getRating(name, value)} />
      )}
      <p className="text-sm text-center">{description}</p>
    </div>
  );
};

/**
 * @param {Object} props
 * @param {string} props.className
 * @param {Object} props.creditScore
 * @param {boolean} props.isBlurred
 * @returns
 */
const CreditProfile = ({ className = "", creditScore, isBlurred }) => {
  const creditSummary = creditScore?.credit_summary || {};
  const {
    credit_utilization: creditUtilizationValue = 0.05, // random value to show "excellent"
    oldest_credit_opened_date: oldestCreditOpenedDate = moment()
      .subtract(7, "years")
      .format("YYYY-MM-DD"), // random date to show "good"
    credit_checks: creditChecksValue = 4, // random value to show "fair"
    total_opened_accounts: openAccountsValue = 7, // random value to show "need work"
  } = creditSummary;

  const creditAgeValue = moment.duration(
    moment().diff(moment(oldestCreditOpenedDate, "YYYY-MM-DD")),
  );

  return (
    <div className={`${className} bg-white py-8 px-6 lg:px-8`}>
      <p className="mb-1.5 text-black text-2xl font-bold">
        Your credit profile
      </p>
      <Xs to="lg">
        <div className="divide-y divide-solid">
          <div className="flex flex-row divide-x divide-solid items-stretch">
            <Factor
              name={FACTORS.UTILIZATION}
              value={creditUtilizationValue}
              valueFormatted={CREDIT_UTILIZATION_FORMATTED(
                creditUtilizationValue,
              )}
              description={FACTOR_DESCRIPTION.UTILIZATION}
              isMobile
              isBlurred={isBlurred}
            />
            <Factor
              name={FACTORS.AGE}
              value={creditAgeValue}
              valueFormatted={CREDIT_AGE_FORMATTED(creditAgeValue)}
              description={FACTOR_DESCRIPTION.AGE}
              isMobile
              isBlurred={isBlurred}
            />
          </div>
          <div className="flex flex-row divide-x divide-solid items-stretch">
            <Factor
              name={FACTORS.CHECKS}
              value={creditChecksValue}
              description={FACTOR_DESCRIPTION.CHECKS}
              isMobile
              isBlurred={isBlurred}
            />
            <Factor
              name={FACTORS.OPEN_ACCOUNTS}
              value={openAccountsValue}
              description={FACTOR_DESCRIPTION.OPEN_ACCOUNTS}
              isMobile
              isBlurred={isBlurred}
            />
          </div>
        </div>
      </Xs>
      <Lg>
        <div className="flex flex-row divide-x divide-solid">
          <Factor
            name={FACTORS.UTILIZATION}
            value={creditUtilizationValue}
            valueFormatted={CREDIT_UTILIZATION_FORMATTED(
              creditUtilizationValue,
            )}
            description={FACTOR_DESCRIPTION.UTILIZATION}
            isBlurred={isBlurred}
          />
          <Factor
            name={FACTORS.AGE}
            value={creditAgeValue}
            valueFormatted={CREDIT_AGE_FORMATTED(creditAgeValue)}
            description={FACTOR_DESCRIPTION.AGE}
            isBlurred={isBlurred}
          />
          <Factor
            name={FACTORS.CHECKS}
            value={creditChecksValue}
            description={FACTOR_DESCRIPTION.CHECKS}
            isBlurred={isBlurred}
          />
          <Factor
            name={FACTORS.OPEN_ACCOUNTS}
            value={openAccountsValue}
            description={FACTOR_DESCRIPTION.OPEN_ACCOUNTS}
            isBlurred={isBlurred}
          />
        </div>
      </Lg>
    </div>
  );
};

export default CreditProfile;
